import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface InvoiceItem {
  id: number;
  particulars: string;
  rate: string;
  weight: string;
  amount: string;
}

interface DeliveryInfo {
  id: number;
  created_at: string;
  calculated_total_weight: number;
  calculated_total_rolls: number;
}

interface InvoiceDelivery {
  id: number;
  delivery_id: number;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  total_weight: string;
  subtotal: string;
  cgst_percentage: string;
  sgst_percentage: string;
  cgst_amount: string;
  sgst_amount: string;
  round_off: string;
  total_amount: string;
  company: {
    id: number;
    name: string;
    address: string;
    phone1: string;
    gst_number: string;
  };
  invoice_items: InvoiceItem[];
  invoice_deliveries: InvoiceDelivery[];
  deliveries: DeliveryInfo[];
  dc_numbers_display: string;
  created_at: string;
}

interface OrgSettings {
  gstin: string;
  phone_number: string;
  org_name: string;
  address: string;
  email: string;
  state_code: string;
  bank_name_and_branch: string;
  bank_account_number: string;
  ifsc_code: string;
}

const invoiceItemSchema = z.object({
  id: z.number().optional(),
  particulars: z.string().min(1, "Particulars is required"),
  rate: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val > 0, "Rate must be positive"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val > 0, "Weight must be positive"),
  _destroy: z.boolean().optional(),
});

const invoiceEditSchema = z.object({
  invoice_date: z.string().min(1, "Invoice date is required"),
  cgst_percentage: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val >= 0 && val <= 100, "Must be between 0 and 100"),
  sgst_percentage: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number())
    .refine((val) => val >= 0 && val <= 100, "Must be between 0 and 100"),
  invoice_items_attributes: z
    .array(invoiceItemSchema)
    .min(1, "At least one item is required"),
});

type InvoiceEditFormData = z.infer<typeof invoiceEditSchema>;

export default function InvoiceDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<InvoiceEditFormData>({
    resolver: zodResolver(invoiceEditSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoice_items_attributes",
  });

  const invoiceItems = watch("invoice_items_attributes");
  const cgstPercentage = watch("cgst_percentage");
  const sgstPercentage = watch("sgst_percentage");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [invoiceRes, orgRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/invoices/${id}`),
        authenticatedFetch(`${API_URL}/organization_settings`),
      ]);

      if (!invoiceRes.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const invoiceData = await invoiceRes.json();
      setInvoice(invoiceData);
      resetForm(invoiceData);

      if (orgRes.ok) {
        const orgData = await orgRes.json();
        setOrgSettings(orgData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (data: Invoice) => {
    reset({
      invoice_date: data.invoice_date,
      cgst_percentage: parseFloat(data.cgst_percentage),
      sgst_percentage: parseFloat(data.sgst_percentage),
      invoice_items_attributes: data.invoice_items.map((item) => ({
        id: item.id,
        particulars: item.particulars || "",
        rate: parseFloat(item.rate),
        weight: parseFloat(item.weight),
      })),
    });
  };

  const getTotalWeight = () => {
    return invoice ? parseFloat(invoice.total_weight) : 0;
  };

  const getItemsWeight = () => {
    return (
      invoiceItems
        ?.filter((item: any) => !item._destroy)
        .reduce((sum, item) => sum + (Number(item?.weight) || 0), 0) || 0
    );
  };

  const calculateSubtotal = () => {
    return (
      invoiceItems
        ?.filter((item: any) => !item._destroy)
        .reduce((sum, item) => {
          const rate = Number(item?.rate) || 0;
          const weight = Number(item?.weight) || 0;
          return sum + rate * weight;
        }, 0) || 0
    );
  };

  const calculateCGST = () => {
    return (calculateSubtotal() * (Number(cgstPercentage) || 0)) / 100;
  };

  const calculateSGST = () => {
    return (calculateSubtotal() * (Number(sgstPercentage) || 0)) / 100;
  };

  const calculateRoundOff = () => {
    const rawTotal = calculateSubtotal() + calculateCGST() + calculateSGST();
    return Math.round(rawTotal) - rawTotal;
  };

  const calculateTotal = () => {
    return Math.round(calculateSubtotal() + calculateCGST() + calculateSGST());
  };

  const isWeightValid = () => {
    const deliveryWeight = getTotalWeight();
    const itemsWeight = getItemsWeight();
    return Math.abs(deliveryWeight - itemsWeight) < 0.01;
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/invoices/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete invoice");
      }

      navigate("/invoices");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const onSubmit = async (data: InvoiceEditFormData) => {
    if (!isWeightValid()) {
      setError(
        `Total weight in items (${getItemsWeight().toFixed(2)} kg) must match delivery weight (${getTotalWeight().toFixed(2)} kg)`
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      const subtotal = calculateSubtotal();
      const cgstAmount = calculateCGST();
      const sgstAmount = calculateSGST();
      const roundOff = calculateRoundOff();
      const totalAmount = calculateTotal();

      const response = await authenticatedFetch(`${API_URL}/invoices/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          invoice: {
            invoice_date: data.invoice_date,
            subtotal: subtotal,
            cgst_percentage: data.cgst_percentage,
            sgst_percentage: data.sgst_percentage,
            cgst_amount: cgstAmount,
            sgst_amount: sgstAmount,
            round_off: roundOff,
            total_amount: totalAmount,
            invoice_items_attributes: data.invoice_items_attributes.map((item) => ({
              id: item.id,
              particulars: item.particulars,
              rate: item.rate,
              weight: item.weight,
              amount: Number(item.rate) * Number(item.weight),
              _destroy: item._destroy,
            })),
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData === "object"
            ? Object.entries(errorData)
                .map(([key, value]) => `${key}: ${value}`)
                .join(", ")
            : JSON.stringify(errorData)
        );
      }

      const updatedInvoice = await response.json();
      setInvoice(updatedInvoice);
      resetForm(updatedInvoice);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (invoice) {
      resetForm(invoice);
    }
    setIsEditing(false);
    setError("");
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatCurrencyWithSymbol = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const numberToWords = (num: number): string => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (num === 0) return "Zero";

    const convertLessThanThousand = (n: number): string => {
      if (n < 20) return ones[n];
      if (n < 100)
        return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " " + convertLessThanThousand(n % 100) : "")
      );
    };

    if (num < 1000) return convertLessThanThousand(num);
    if (num < 100000)
      return (
        convertLessThanThousand(Math.floor(num / 1000)) +
        " Thousand" +
        (num % 1000 ? " " + convertLessThanThousand(num % 1000) : "")
      );
    if (num < 10000000)
      return (
        convertLessThanThousand(Math.floor(num / 100000)) +
        " Lakh" +
        (num % 100000
          ? " " +
            convertLessThanThousand(Math.floor((num % 100000) / 1000)) +
            (num % 1000 ? " Thousand" : "") +
            (num % 1000 ? " " + convertLessThanThousand(num % 1000) : "")
          : "")
      );

    return String(num);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Invoice not found</Alert>
      </Container>
    );
  }

  // Mobile Edit Item Card
  const renderMobileEditItem = (field: any, index: number) => {
    const item = invoiceItems?.[index];
    if ((item as any)?._destroy) return null;

    const rate = Number(item?.rate) || 0;
    const weight = Number(item?.weight) || 0;
    const amount = rate * weight;

    return (
      <Card key={field.id} variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle2" color="primary">
              Item {index + 1}
            </Typography>
            <IconButton
              color="error"
              onClick={() => {
                const currentItem = invoiceItems[index];
                if ((currentItem as any)?.id) {
                  const currentItems = [...invoiceItems];
                  (currentItems[index] as any)._destroy = true;
                  reset({ ...watch(), invoice_items_attributes: currentItems });
                } else {
                  remove(index);
                }
              }}
              disabled={
                fields.filter((_, i) => !(invoiceItems[i] as any)?._destroy).length === 1
              }
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Controller
            name={`invoice_items_attributes.${index}.particulars`}
            control={control}
            render={({ field: fieldProps, fieldState }) => (
              <TextField
                {...fieldProps}
                label="Particulars"
                placeholder="e.g., Pallava 40s VL"
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                multiline
                maxRows={2}
                sx={{ mb: 2 }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name={`invoice_items_attributes.${index}.rate`}
                control={control}
                render={({
                  field: { onChange, value, ...fieldProps },
                  fieldState,
                }) => (
                  <TextField
                    {...fieldProps}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    label="Rate"
                    type="number"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name={`invoice_items_attributes.${index}.weight`}
                control={control}
                render={({
                  field: { onChange, value, ...fieldProps },
                  fieldState,
                }) => (
                  <TextField
                    {...fieldProps}
                    value={value || ""}
                    onChange={(e) =>
                      onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    label="Weight (kg)"
                    type="number"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, pt: 1, borderTop: "1px dashed", borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              Amount: <strong>{formatCurrencyWithSymbol(amount)}</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Edit Mode
  if (isEditing) {
    return (
      <Container maxWidth="md" sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}>
        <Card>
          <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Typography variant={isMobile ? "h6" : "h5"}>
                Edit Invoice #{invoice.invoice_number}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {/* Invoice Date */}
              <Controller
                name="invoice_date"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Invoice Date"
                    type="date"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />

              {/* Delivery Info - Read Only */}
              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Selected DCs:</strong> {invoice.dc_numbers_display}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Weight:</strong> {formatCurrency(invoice.total_weight)} kg
                </Typography>
              </Box>

              {/* Rate Items */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Invoice Items</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      append({
                        particulars: "",
                        rate: undefined as any,
                        weight: undefined as any,
                      })
                    }
                  >
                    Add Item
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Total must equal {formatCurrency(invoice.total_weight)} kg
                </Alert>

                {isMobile ? (
                  // Mobile: Card-based layout
                  <Box>
                    {fields.map((field, index) => renderMobileEditItem(field, index))}
                  </Box>
                ) : (
                  // Desktop: Table layout
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>Particulars</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Rate (₹/kg)</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Weight (kg)</TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            Amount (₹)
                          </TableCell>
                          <TableCell width={60}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map((field, index) => {
                          const item = invoiceItems?.[index];
                          if ((item as any)?._destroy) return null;

                          const rate = Number(item?.rate) || 0;
                          const weight = Number(item?.weight) || 0;
                          const amount = rate * weight;

                          return (
                            <TableRow key={field.id}>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.particulars`}
                                  control={control}
                                  render={({ field: fieldProps, fieldState }) => (
                                    <TextField
                                      {...fieldProps}
                                      placeholder="e.g., Pallava 40s VL"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                      multiline
                                      maxRows={2}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.rate`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value ? Number(e.target.value) : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">₹</InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <Controller
                                  name={`invoice_items_attributes.${index}.weight`}
                                  control={control}
                                  render={({
                                    field: { onChange, value, ...fieldProps },
                                    fieldState,
                                  }) => (
                                    <TextField
                                      {...fieldProps}
                                      value={value || ""}
                                      onChange={(e) =>
                                        onChange(
                                          e.target.value ? Number(e.target.value) : undefined
                                        )
                                      }
                                      type="number"
                                      size="small"
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      fullWidth
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrencyWithSymbol(amount)}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    const currentItem = invoiceItems[index];
                                    if ((currentItem as any)?.id) {
                                      const currentItems = [...invoiceItems];
                                      (currentItems[index] as any)._destroy = true;
                                      reset({ ...watch(), invoice_items_attributes: currentItems });
                                    } else {
                                      remove(index);
                                    }
                                  }}
                                  disabled={
                                    fields.filter(
                                      (_, i) => !(invoiceItems[i] as any)?._destroy
                                    ).length === 1
                                  }
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!isWeightValid() && getItemsWeight() > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Weight mismatch: Items total ({getItemsWeight().toFixed(2)} kg) ≠
                    Delivery total ({getTotalWeight().toFixed(2)} kg)
                  </Alert>
                )}
              </Box>

              {/* Tax Configuration */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Tax Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Controller
                      name="cgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...fieldProps }, fieldState }) => (
                        <TextField
                          {...fieldProps}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="CGST %"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="sgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...fieldProps }, fieldState }) => (
                        <TextField
                          {...fieldProps}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="SGST %"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Summary */}
              <Box sx={{ mt: 3, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Invoice Summary
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{formatCurrencyWithSymbol(calculateSubtotal())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">CGST ({cgstPercentage}%):</Typography>
                    <Typography variant="body2">{formatCurrencyWithSymbol(calculateCGST())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">SGST ({sgstPercentage}%):</Typography>
                    <Typography variant="body2">{formatCurrencyWithSymbol(calculateSGST())}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Round Off:</Typography>
                    <Typography variant="body2">{formatCurrencyWithSymbol(calculateRoundOff())}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {formatCurrencyWithSymbol(calculateTotal())}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  fullWidth
                  disabled={saving || !isWeightValid()}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  fullWidth
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Mobile Responsive View
  const renderMobileView = () => (
    <Box className="no-print">
      {/* Action Buttons */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/invoices")}
          variant="outlined"
          fullWidth
        >
          Back to Invoices
        </Button>
        <Stack direction="row" spacing={1}>
          {canUpdate('invoices') && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              variant="contained"
              fullWidth
            >
              Edit
            </Button>
          )}
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="outlined"
            fullWidth
          >
            Print
          </Button>
          {canDelete('invoices') && (
            <IconButton onClick={handleDelete} color="error" title="Delete">
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Invoice Header Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" component="h1">
              Invoice #{invoice.invoice_number}
            </Typography>
          </Box>
          <Chip
            label={`${new Date(invoice.invoice_date).toLocaleDateString("en-IN")}`}
            size="small"
            sx={{ mb: 2 }}
          />
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Total Weight</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatCurrency(invoice.total_weight)} kg
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">DC Numbers</Typography>
              <Typography variant="body1" fontWeight="medium">
                {invoice.dc_numbers_display}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Customer Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <BusinessIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Customer
            </Typography>
          </Box>
          <Typography
            variant="body1"
            fontWeight="medium"
            onClick={() => navigate(`/companies/${invoice.company.id}`)}
            sx={{
              cursor: "pointer",
              color: "primary.main",
              textDecoration: "underline",
              "&:hover": { color: "primary.dark" },
            }}
          >
            {invoice.company.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", mt: 0.5 }}>
            {invoice.company.address}
          </Typography>
          {invoice.company.gst_number && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>GSTIN:</strong> {invoice.company.gst_number}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Invoice Items */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LocalShippingIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Items
            </Typography>
          </Box>
          {invoice.invoice_items.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                {item.particulars}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Rate</Typography>
                  <Typography variant="body2">₹{formatCurrency(item.rate)}/kg</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Weight</Typography>
                  <Typography variant="body2">{formatCurrency(item.weight)} kg</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography variant="body2" fontWeight="medium">₹{formatCurrency(item.amount)}</Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card sx={{ mb: 2, bgcolor: "primary.50" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Invoice Summary
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">₹{formatCurrency(invoice.subtotal)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">CGST ({invoice.cgst_percentage}%)</Typography>
              <Typography variant="body2">₹{formatCurrency(invoice.cgst_amount)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">SGST ({invoice.sgst_percentage}%)</Typography>
              <Typography variant="body2">₹{formatCurrency(invoice.sgst_amount)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Round Off</Typography>
              <Typography variant="body2">₹{formatCurrency(invoice.round_off)}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6" color="primary.main">
                ₹{formatCurrency(invoice.total_amount)}
              </Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {numberToWords(Math.round(parseFloat(invoice.total_amount)))} Rupees Only
          </Typography>
        </CardContent>
      </Card>

      {/* Bank Details Card */}
      {orgSettings && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <AccountBalanceIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Bank Details
              </Typography>
            </Box>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Bank:</strong> {orgSettings.bank_name_and_branch}
              </Typography>
              <Typography variant="body2">
                <strong>Account:</strong> {orgSettings.bank_account_number}
              </Typography>
              <Typography variant="body2">
                <strong>IFSC:</strong> {orgSettings.ifsc_code}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Organization Details Card */}
      {orgSettings && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              From: {orgSettings.org_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orgSettings.address}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              GSTIN: {orgSettings.gstin}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  // Desktop Printable View (also shown in print)
  const renderPrintableView = () => (
    <Paper
      elevation={3}
      className="printable-invoice"
      sx={{
        p: 3,
        display: { xs: "none", md: "block" },
        "@media print": {
          display: "block !important",
          boxShadow: "none",
          p: 0,
        },
      }}
    >
      {/* Letterhead */}
      <Box
        sx={{
          border: "2px solid #1976d2",
          p: 2,
          mb: 0,
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            GSTIN : {orgSettings?.gstin || "N/A"}
          </Typography>
          <Box
            sx={{
              border: "1px solid #000",
              px: 2,
              py: 0.5,
              fontWeight: "bold",
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              GST INVOICE
            </Typography>
          </Box>
          <Typography variant="body2">
            Cell : {orgSettings?.phone_number || "N/A"}
          </Typography>
        </Box>

        {/* Company Name */}
        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          {orgSettings?.org_name || "DEVI IMPEX"}
        </Typography>

        {/* Address */}
        <Typography variant="body2" align="center" sx={{ mb: 2 }}>
          {orgSettings?.address || "N/A"}
          {orgSettings?.email && ` E-mail : ${orgSettings.email}`}
        </Typography>

        {/* Customer & Invoice Details */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Left Side - Customer Info */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>To:</strong> {invoice.company.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: "pre-line" }}>
              {invoice.company.address}
            </Typography>
            <Typography variant="body2">
              <strong>GSTIN:</strong> {invoice.company.gst_number || "N/A"}
            </Typography>
          </Box>

          {/* Right Side - Invoice Details Table */}
          <Box sx={{ width: 220 }}>
            <Table size="small" sx={{ border: "1px solid #000" }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    Invoice No.
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    {invoice.invoice_number}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    {new Date(invoice.invoice_date).toLocaleDateString("en-IN")}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    State Code
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    {orgSettings?.state_code || "33"} - Tamilnadu
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    Party DC No.
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    {invoice.dc_numbers_display}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Items Table */}
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small" sx={{ border: "1px solid #000" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ border: "1px solid #000", fontWeight: "bold", width: 60 }}
                >
                  Rate
                </TableCell>
                <TableCell
                  sx={{ border: "1px solid #000", fontWeight: "bold", flex: 1 }}
                >
                  Particulars
                </TableCell>
                <TableCell
                  sx={{ border: "1px solid #000", fontWeight: "bold", width: 70 }}
                >
                  DC No.
                </TableCell>
                <TableCell
                  sx={{
                    border: "1px solid #000",
                    fontWeight: "bold",
                    width: 100,
                    textAlign: "right",
                  }}
                >
                  Kilo Grams
                </TableCell>
                <TableCell
                  sx={{
                    border: "1px solid #000",
                    fontWeight: "bold",
                    width: 120,
                    textAlign: "right",
                  }}
                >
                  Rs. Ps.
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Rate Items */}
              {invoice.invoice_items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ border: "1px solid #000", py: 1 }}>
                    {formatCurrency(item.rate)}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 1 }}>
                    {item.particulars}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", py: 1 }}>
                    {index === 0 && invoice.dc_numbers_display}
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #000", py: 1, textAlign: "right" }}
                  >
                    {formatCurrency(item.weight)}
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #000", py: 1, textAlign: "right" }}
                  >
                    {formatCurrency(item.amount)}
                  </TableCell>
                </TableRow>
              ))}

              {/* Empty rows for visual spacing */}
              {Array(Math.max(0, 4 - invoice.invoice_items.length))
                .fill(0)
                .map((_, idx) => (
                  <TableRow key={`empty-${idx}`}>
                    <TableCell sx={{ border: "1px solid #000", py: 2 }}></TableCell>
                    <TableCell sx={{ border: "1px solid #000", py: 2 }}></TableCell>
                    <TableCell sx={{ border: "1px solid #000", py: 2 }}></TableCell>
                    <TableCell sx={{ border: "1px solid #000", py: 2 }}></TableCell>
                    <TableCell sx={{ border: "1px solid #000", py: 2 }}></TableCell>
                  </TableRow>
                ))}

              {/* Total Weight Row */}
              <TableRow>
                <TableCell sx={{ border: "1px solid #000" }} colSpan={3}></TableCell>
                <TableCell
                  sx={{
                    border: "1px solid #000",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  {formatCurrency(invoice.total_weight)}
                </TableCell>
                <TableCell
                  sx={{
                    border: "1px solid #000",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  {formatCurrency(invoice.subtotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Section - Bank Details & Totals */}
        <Box sx={{ display: "flex", mt: 2 }}>
          {/* Bank Details */}
          <Box sx={{ flex: 1, border: "1px solid #000", p: 1.5 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              BANK DETAILS:
            </Typography>
            <Typography variant="body2">
              Bank Name & Branch : {orgSettings?.bank_name_and_branch || "N/A"}
            </Typography>
            <Typography variant="body2">
              Bank Account Number :{" "}
              <span style={{ fontFamily: "monospace", letterSpacing: 1 }}>
                {orgSettings?.bank_account_number || "N/A"}
              </span>
            </Typography>
            <Typography variant="body2">
              Bank Branch IFSC : {orgSettings?.ifsc_code || "N/A"}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                Rupees:{" "}
                <strong>
                  {numberToWords(Math.round(parseFloat(invoice.total_amount)))} Only
                </strong>
              </Typography>
            </Box>
          </Box>

          {/* Totals */}
          <Box sx={{ width: 200 }}>
            <Table size="small" sx={{ border: "1px solid #000" }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    TOTAL
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {formatCurrency(invoice.subtotal)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    CGST {invoice.cgst_percentage}%
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {formatCurrency(invoice.cgst_amount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    SGST {invoice.sgst_percentage}%
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {formatCurrency(invoice.sgst_amount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", py: 0.5 }}>
                    Round off
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      textAlign: "right",
                    }}
                  >
                    {formatCurrency(invoice.round_off)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      fontWeight: "bold",
                    }}
                  >
                    Total Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      py: 0.5,
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(invoice.total_amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Footer */}
        <Box className="invoice-footer" sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
          <Box>
            <Typography variant="body2">Common Seal</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="caption">
              Certified that the particulars given above are true and correct
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
              For {orgSettings?.org_name || "DEVI IMPEX"}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2">Authorised Signatory</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  // View Mode
  return (
    <Container maxWidth="md" sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Desktop Action Buttons - Hidden in Print */}
      <Box
        className="no-print"
        sx={{
          mb: 2,
          display: { xs: "none", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/invoices")}
          variant="outlined"
        >
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
            variant="contained"
          >
            Edit
          </Button>
          <IconButton onClick={handlePrint} color="primary" title="Print">
            <PrintIcon />
          </IconButton>
          <IconButton onClick={handleDelete} color="error" title="Delete">
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} className="no-print">
          {error}
        </Alert>
      )}

      {/* Mobile View - Responsive Cards */}
      {isMobile && renderMobileView()}

      {/* Desktop View - Printable Invoice */}
      {renderPrintableView()}

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 12mm;
            }
            
            html, body {
              height: 100%;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            body * {
              visibility: hidden;
            }
            
            .printable-invoice, .printable-invoice * {
              visibility: visible !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            .MuiContainer-root {
              max-width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .printable-invoice {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              box-shadow: none !important;
              padding: 0 !important;
            }
            
            .printable-invoice .invoice-footer {
              margin-top: 16px !important;
            }
            
            .printable-invoice .invoice-footer .MuiBox-root:last-child {
              margin-top: 20px !important;
            }
          }
        `}
      </style>
    </Container>
  );
}
